import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { DEFAULT_VALUES } from '../../../../libraries/web-framework/src/localization/values';

import type { BulletOrIconListItem } from './strings';
import type { SelectableAppTypes, SelectableCollectionTypes } from '../context/types';

export type AppEntry = {
	label: string;
	iconUrl: string;
	description: string;
};

export type CollectionEntry = {
	lockupUrl: string;
	/** Rich description shown on the allowance-step choice card. */
	description: string | ReactNode;
	/** Short plain-text tagline shown on the summary-step product card. */
	shortDescription: string;
	list: BulletOrIconListItem[];
};

export const useAppDescriptions = (): Record<SelectableAppTypes, AppEntry> => {
	const intl = useIntl();
	return useMemo<Record<SelectableAppTypes, AppEntry>>(
		() => ({
			jira: {
				label: 'Jira',
				iconUrl:
					'https://dam-cdn.atl.orangelogic.com/AssetLink/478vdip7223r1idsl83luxcl3x6d8153.svg',
				description: intl.formatMessage({
					id: 'ubp.calculator.apps.jira.description',
					defaultMessage: 'Flexible project management',
				}),
			},
			confluence: {
				label: 'Confluence',
				iconUrl:
					'https://dam-cdn.atl.orangelogic.com/AssetLink/yria5c22578g421umx80xu14m77622ej.svg',
				description: intl.formatMessage({
					id: 'ubp.calculator.apps.confluence.description',
					defaultMessage: 'Knowledge, all in one place',
				}),
			},
			jpd: {
				label: 'Jira Product Discovery',
				iconUrl:
					'https://dam-cdn.atl.orangelogic.com/AssetLink/06wk2ma2fw3fde5d2pu7r04r770862g4.svg',
				description: intl.formatMessage({
					id: 'ubp.calculator.apps.jpd.description',
					defaultMessage: 'Capture and prioritize ideas',
				}),
			},
			jsm: {
				label: 'Jira Service Management',
				iconUrl:
					'https://dam-cdn.atl.orangelogic.com/AssetLink/bafh8247s17543215301yjy1iaw6lgon.svg',
				description: intl.formatMessage({
					id: 'ubp.calculator.apps.jsm.description',
					defaultMessage: 'High-velocity service delivery',
				}),
			},
		}),
		[intl],
	);
};

export const useCollectionDescriptions = (): Record<SelectableCollectionTypes, CollectionEntry> => {
	const intl = useIntl();
	return useMemo<Record<SelectableCollectionTypes, CollectionEntry>>(
		() => ({
			teamworkCollection: {
				lockupUrl: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT1ULE7.svg',
				shortDescription: intl.formatMessage({
					id: 'ubp.calculator.collections.teamwork.shortDescription',
					defaultMessage: 'Plan, document, and deliver together',
				}),
				description: (
					<FormattedMessage
						id="ubp.calculator.collections.teamwork.description"
						defaultMessage="Everything your team needs to plan, document, and deliver—all in one place.<br></br><br></br>Includes licensing for the following apps:"
						values={DEFAULT_VALUES}
					/>
				),
				list: [
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.collections.teamwork.list.one',
							defaultMessage: 'Jira; For flexible project management',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.collections.teamwork.list.two',
							defaultMessage: 'Confluence; Knowledge all in one place',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.collections.teamwork.list.three',
							defaultMessage: 'Loom; AI-powered video messages',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.collections.teamwork.list.four',
							defaultMessage: 'Rovo; Transform teamwork with AI',
						}),
					},
				],
			},
			serviceCollection: {
				lockupUrl: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12O5B2.svg',
				shortDescription: intl.formatMessage({
					id: 'ubp.calculator.collections.service.shortDescription',
					defaultMessage: 'High-velocity service delivery, at scale',
				}),
				description: (
					<FormattedMessage
						id="ubp.calculator.collections.service.description"
						defaultMessage="Set strategic priorities, allocate investments, and see real-time portfolio progress in a single view.<br></br><br></br>Includes licensing for the following apps:"
						values={DEFAULT_VALUES}
					/>
				),
				list: [
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.collections.serviceCollection.list.one',
							defaultMessage: 'Jira Service Management; Deliver service at high velocity',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.collections.serviceCollection.list.two',
							defaultMessage: 'Customer Service Management; Customer experiences reimagined',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.collections.serviceCollection.list.three',
							defaultMessage: 'Assets; Track what matters',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.collections.serviceCollection.list.four',
							defaultMessage: 'Rovo; Team up with AI',
						}),
					},
				],
			},
		}),
		[intl],
	);
};
